/**
 * Registry operations (a reconciliation difference, a poll message) and market administration
 * (a backorder, an auction). Each asks for a reason, kept in the audit log.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../api';
import type { IssueOutcome } from '../../api/areas/operator';
import type { PollMessage, ReconciliationIssue } from '../../api/areas/registry';
import { Callout, Field } from '../../components/blocks';
import { useWrite, WriteError } from '../../components/Write';
import { ActionDialog, FormFooter, ReasonAction, ReasonField, type DialogBodyProps } from './shared';

const OUTCOMES: IssueOutcome[] = ['REGISTRY_CORRECT', 'FIXED', 'NO_ACTION'];
const OPEN_ISSUE = ['OPEN', 'AUTO_RECONCILING', 'MANUAL_REVIEW'];

function ResolveForm({ close, changed, issue }: DialogBodyProps & { issue: ReconciliationIssue }) {
  const { t } = useTranslation('operator');
  const [outcome, setOutcome] = useState<IssueOutcome | ''>('');
  const [reason, setReason] = useState('');
  const w = useWrite((b: { outcome: IssueOutcome; reason: string }, opts) => api.resolveReconciliationIssue(issue.id, b, opts));
  const options = OUTCOMES.filter((o) => o !== 'REGISTRY_CORRECT' || issue.domain_id);
  return (
    <form
      className="grid gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (outcome) void w.run({ outcome, reason: reason.trim() }).then((r) => r && changed());
      }}
    >
      {w.state.phase === 'done' ? (
        <Callout tone="ok">{t('registry.resolved')}</Callout>
      ) : (
        <>
          <Field label={t('registry.outcome')}>
            <div className="grid gap-2">
              {options.map((o) => (
                // eslint-disable-next-line jsx-a11y/label-has-associated-control -- the radio is the child
                <label key={o} className="flex items-start gap-2 text-sm">
                  <input type="radio" name="outcome" className="mt-1" checked={outcome === o} onChange={() => setOutcome(o)} />
                  {t(`registry.outcomes.${o}`)}
                </label>
              ))}
            </div>
          </Field>
          {issue.registry_transaction_id ? <Callout tone="info">{t('registry.commandStays')}</Callout> : null}
          <ReasonField value={reason} onChange={setReason} />
        </>
      )}
      {w.state.phase === 'error' ? <WriteError error={w.state.error} /> : null}
      <FormFooter busy={w.busy} disabled={!outcome || !reason.trim()} submit={w.state.phase === 'done' ? undefined : t('registry.resolve')} onClose={close} />
    </form>
  );
}

/** "Resolve" on a reconciliation difference that is still open. */
export function ResolveIssueButton({ issue, onChanged }: { issue: ReconciliationIssue; onChanged: () => void }) {
  const { t } = useTranslation('operator');
  if (!OPEN_ISSUE.includes(issue.state)) return null;
  return (
    <ActionDialog label={t('registry.resolve')} variant="default" title={t('registry.resolveTitle')} description={t('registry.resolveDescription')} wide onChanged={onChanged}>
      {(p) => <ResolveForm {...p} issue={issue} />}
    </ActionDialog>
  );
}

/** "Confirm to the registry" and "Mark as handled" on one poll message. */
export function PollMessageActions({ message, onChanged }: { message: PollMessage; onChanged: () => void }) {
  const { t } = useTranslation('operator');
  return (
    <>
      {!message.acked_at ? (
        <ReasonAction
          label={t('registry.ack')}
          title={t('registry.ackTitle')}
          description={t('registry.ackDescription')}
          run={(reason) => api.acknowledgePollMessage(message.id, { reason })}
          done={() => t('registry.acked')}
          onChanged={onChanged}
        />
      ) : null}
      {!message.processed_at ? (
        <ReasonAction
          label={t('registry.processed')}
          title={t('registry.processedTitle')}
          description={t('registry.processedDescription')}
          variant="default"
          run={(reason, opts) => api.markPollMessageProcessed(message.id, { reason }, opts)}
          done={() => t('registry.markedProcessed')}
          onChanged={onChanged}
        />
      ) : null}
    </>
  );
}

/** "Cancel the backorder" (an ACTIVE one only). */
export function CancelBackorderButton({ backorder, onChanged }: { backorder: { id: string; status: string; domain_name: string }; onChanged: () => void }) {
  const { t } = useTranslation('operator');
  if (backorder.status !== 'ACTIVE') return null;
  return (
    <ReasonAction
      label={t('market.cancelBackorder')}
      title={`${t('market.cancelBackorder')} · ${backorder.domain_name}`}
      warning={t('market.cancelBackorderDescription')}
      danger
      run={(reason, opts) => api.cancelBackorder(backorder.id, { reason }, opts)}
      done={() => t('market.backorderCancelled')}
      onChanged={onChanged}
    />
  );
}

/** "Open now" / "Close now" on an auction whose worker is late. */
export function AuctionTimingButtons({ auction, onChanged }: { auction: { id: string; status: string; domain_name: string }; onChanged: () => void }) {
  const { t } = useTranslation('operator');
  const { t: tm } = useTranslation('market');
  const live = auction.status === 'SCHEDULED' || auction.status === 'OPEN';
  if (!live) return null;
  return (
    <>
      {auction.status === 'SCHEDULED' ? (
        <ReasonAction
          label={t('market.open')}
          title={`${t('market.openTitle')} · ${auction.domain_name}`}
          description={t('market.openDescription')}
          run={(reason, opts) => api.openAuction(auction.id, { reason }, opts)}
          done={() => t('market.opened')}
          onChanged={onChanged}
        />
      ) : null}
      <ReasonAction
        label={t('market.close')}
        title={`${t('market.closeTitle')} · ${auction.domain_name}`}
        description={t('market.closeDescription')}
        run={(reason, opts) => api.closeAuction(auction.id, { reason }, opts)}
        done={(r) => t('market.closed', { status: tm(`enum.auctionStatus.${r.status}` as "enum.auctionStatus.OPEN", { defaultValue: r.status }) })}
        onChanged={onChanged}
      />
    </>
  );
}
